import { useState, useEffect, useCallback } from 'react';
import { useCadrageStore } from '../stores/cadrageStore'; // Utiliser le store Cadrage
import userService from '../services/userService';
import cadrageService from '../services/cadrageService';
import i18n from '../i18n/config';
import { User } from '../types';

interface UseCadrageTeamManagementReturn {
  teamMembers: User[] | undefined;
  availableUsers: User[];
  isLoading: boolean;
  error: string | null;
  addMember: (userId: string) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
}

/**
 * Hook pour gérer l'équipe associée à un Cadrage.
 * S'inspire de useAuditTeamManagement mais utilise le store et les endpoints Cadrage.
 * @param cadrageId - L'ID du cadrage concerné.
 */
export function useCadrageTeamManagement(cadrageId: string | undefined): UseCadrageTeamManagementReturn {
  // Utiliser le store Cadrage
  const cadrageData = useCadrageStore((state) => state.cadrageData);
  const loadCadrageData = useCadrageStore((state) => state.loadCadrageData);
  const storeIsLoading = useCadrageStore((state) => state.isLoading); // Renommé pour clarté

  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [teamMembers, setTeamMembers] = useState<User[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false); // État de chargement local pour les actions du hook
  const [error, setError] = useState<string | null>(null);

  // Effet pour mettre à jour teamMembers local lorsque cadrageData change
  useEffect(() => {
    // Utiliser 'members' conformément au type CadrageReadWithTeam
    setTeamMembers(cadrageData?.members || []); // Utiliser un tableau vide par défaut
  }, [cadrageData]);

  // Fetch available users (identique à useAuditTeamManagement)
  const fetchAvailableUsers = useCallback(async () => {
    // Pas besoin de cadrageId pour récupérer tous les utilisateurs
    setIsLoading(true);
    setError(null);
    try {
      const users = await userService.fetchAllUsers();
      setAvailableUsers(users || []);
    } catch (err: any) {
      console.error("Error fetching available users:", err);
      setError(err.response?.data?.detail || err.message || i18n.t('errors.team.fetchUsersFailed'));
      setAvailableUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableUsers();
  }, [fetchAvailableUsers]);

  // Add member function
  const addMember = useCallback(async (userId: string) => {
    if (!cadrageId) {
      const message = i18n.t('errors.cadrage.missingId');
      setError(message);
      return Promise.reject(new Error(message)); // Rejeter la promesse
    }
    setIsLoading(true);
    setError(null);
    try {
      // Utiliser l'endpoint correct avec "/members/"
      await cadrageService.addTeamMember(cadrageId, userId);
      // Optimistic update: reflect the change locally
      const addedUser = availableUsers.find(u => u.id === userId);
      if (addedUser) {
        setTeamMembers(prev => {
          const current = prev || [];
          if (current.some(u => u.id === addedUser.id)) return current;
          return [...current, addedUser];
        });
      }
      // Attempt to refetch for eventual consistency
      await loadCadrageData(cadrageId);
    } catch (err: any) {
      console.error("Error adding team member to cadrage:", err);
      const errorMsg = err.response?.data?.detail || err.message || i18n.t('cadrageDetail.addMemberError');
      setError(errorMsg);
      throw new Error(errorMsg); // Propager l'erreur
    } finally {
      setIsLoading(false);
    }
  }, [cadrageId, loadCadrageData, availableUsers]);

  // Remove member function
  const removeMember = useCallback(async (userId: string) => {
    if (!cadrageId) {
      const message = i18n.t('errors.cadrage.missingId');
      setError(message);
      return Promise.reject(new Error(message)); // Rejeter la promesse
    }
    setIsLoading(true);
    setError(null);
    try {
      // Utiliser l'endpoint correct avec "/members/"
      await cadrageService.removeTeamMember(cadrageId, userId);
      // Optimistic removal
      setTeamMembers(prev => (prev || []).filter(u => u.id !== userId));
      // Attempt to refetch for eventual consistency
      await loadCadrageData(cadrageId);
    } catch (err: any) {
      console.error("Error removing team member from cadrage:", err);
      const errorMsg = err.response?.data?.detail || err.message || i18n.t('cadrageDetail.removeMemberError');
      setError(errorMsg);
      throw new Error(errorMsg); // Propager l'erreur
    } finally {
      setIsLoading(false);
    }
  }, [cadrageId, loadCadrageData]);

  // Combiner l'état de chargement local et celui du store
  const combinedIsLoading = isLoading || storeIsLoading;

  return {
    teamMembers,
    availableUsers,
    isLoading: combinedIsLoading,
    error,
    addMember,
    removeMember,
  };
}
